import type {
    DestinationMapBoundingBox,
    DestinationMapCoordinates,
    LngLatBoundsLike,
    ViewportBounds
} from "./SmartGuidePermanentMapTypes";

const metersPerLatitudeDegree = 111320;
const bboxMultiple = 0.2;

export function boundsAround(center: DestinationMapCoordinates, meters: number): LngLatBoundsLike {
    const latitudeDelta = meters / metersPerLatitudeDegree;
    const longitudeDelta = meters / (metersPerLatitudeDegree * Math.cos(center.latitude * Math.PI / 180));

    return [
        [center.longitude - longitudeDelta, center.latitude - latitudeDelta],
        [center.longitude + longitudeDelta, center.latitude + latitudeDelta]
    ];
}

export function destinationBoundingBoxToLngLatBounds(boundingBox: DestinationMapBoundingBox): LngLatBoundsLike {
    return [
        [boundingBox.southWest.longitude, boundingBox.southWest.latitude],
        [boundingBox.northEast.longitude, boundingBox.northEast.latitude]
    ];
}

export function bboxPrecisionForZoom(zoom: number): number {
    return Math.max(0, Math.round(zoom * bboxMultiple));
}

export function viewportBoundsToBboxes(bounds: ViewportBounds, zoom: number): string[] {
    const precision = bboxPrecisionForZoom(zoom);
    const south = floorToPrecision(bounds.south, precision);
    const north = ceilToPrecision(bounds.north, precision);
    const bbox = (west: number, east: number): string =>
        [west, south, east, north].map(value => formatNumber(value, precision)).join(",");
    let west = floorToPrecision(bounds.west, precision);
    let east = ceilToPrecision(bounds.east, precision);

    if (east - west >= 360) {
        return [bbox(-180, 0), bbox(0, 180)];
    }

    const repeatedWorldOffset = Math.floor((west + 180) / 360) * 360;
    west -= repeatedWorldOffset;
    east -= repeatedWorldOffset;
    if (east <= 180) {
        return [bbox(west, east)];
    }

    const easternWorld = { west, east: 180 };
    const westernWorld = { west: -180, east: east - 360 };
    const orderedWorlds = [easternWorld, westernWorld]
        .sort((left, right) => (right.east - right.west) - (left.east - left.west));
    return orderedWorlds.map(world => bbox(world.west, world.east));
}

export function hasUsableCoordinates(coordinates?: DestinationMapCoordinates | null): coordinates is DestinationMapCoordinates {
    return Number.isFinite(coordinates?.latitude) && Number.isFinite(coordinates?.longitude);
}

export function hasUsableBoundingBox(boundingBox?: DestinationMapBoundingBox | null): boundingBox is DestinationMapBoundingBox {
    return hasUsableCoordinates(boundingBox?.northEast) && hasUsableCoordinates(boundingBox?.southWest);
}

export function hasUsableViewportBounds(bounds?: ViewportBounds | null): bounds is ViewportBounds {
    return Number.isFinite(bounds?.west) &&
        Number.isFinite(bounds?.south) &&
        Number.isFinite(bounds?.east) &&
        Number.isFinite(bounds?.north) &&
        bounds!.west < bounds!.east &&
        bounds!.south < bounds!.north;
}

function floorToPrecision(value: number, precision: number): number {
    const factor = 10 ** precision;
    return Math.floor(value * factor) / factor;
}

function ceilToPrecision(value: number, precision: number): number {
    const factor = 10 ** precision;
    return Math.ceil(value * factor) / factor;
}

function formatNumber(value: number, precision: number): string {
    return Number(value.toFixed(precision)).toString();
}
